import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../../context/AuthContext";
import { useNotifications } from "../../../context/NotificationContext";
import { onboardingApi } from "../../../api/packageApi";
import { websiteApi } from "../../../api/websiteApi";
import { paymentApi } from "../../../api/paymentApi";
import useOnboardingDraft from "../../../hooks/useOnboardingDraft";
import BusinessProfileStep from "./BusinessProfileStep";
import { DEFAULT_CONTRACT_TEMPLATES } from "./contractTemplates";
import DomainPriceNoticeModal from "./DomainPriceNoticeModal";
import {
  DEFAULT_WEBSITE_ONBOARDING_QUESTIONS,
  parseWebsiteOnboardingQuestions,
} from "./websiteOnboardingQuestions";
import "./RequestStore.css";
import "./WebsiteOnboarding.css";
import "../../../components/common/Layout/Requeststore.mobile.css";

const FEATURE_LIMIT = 3;
const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;
const URL_PATTERN = /^https?:\/\/\S+\.\S+/i;

const DEFAULT_WEBSITE_STEPS = [
  {
    id: "package",
    name: "Package",
    title: "Choose Your Package",
    description: "Choose the website package that suits your project.",
    enabled: true,
    required: true,
    order: 1,
    icon: "fa-box-open",
  },
  {
    id: "business",
    name: "Business",
    title: "Tell Us About Your Business",
    description:
      "Share your business details and website project requirements.",
    enabled: true,
    required: true,
    order: 2,
    icon: "fa-building",
  },
  {
    id: "domain",
    name: "Domain",
    title: "Domain Setup",
    description: "Select a domain for your website and check availability.",
    enabled: true,
    required: true,
    order: 3,
    icon: "fa-globe",
  },
  {
    id: "agreement",
    name: "Agreement",
    title: "Service Agreement",
    description: "Review and accept the website service agreement.",
    enabled: true,
    required: true,
    order: 4,
    icon: "fa-file-signature",
  },
  {
    id: "invoice",
    name: "Invoice",
    title: "Invoice & Payment",
    description: "Review your invoice and choose a payment method.",
    enabled: true,
    required: true,
    order: 5,
    icon: "fa-file-invoice-dollar",
  },
];

const formatPrice = (pkg) =>
  pkg?.IsQuoteOnly
    ? "Custom quote"
    : `R${Number(pkg?.Price || 0).toLocaleString("en-ZA")}`;

const FieldError = ({ id, message }) =>
  message ? (
    <span className="field-error" id={id}>
      {message}
    </span>
  ) : null;

const StepIcon = ({ icon, fallback }) =>
  typeof icon === "string" && icon.startsWith("fa-") ? (
    <i className={`fas ${icon}`} aria-hidden="true" />
  ) : (
    <>{icon || fallback}</>
  );

const slideVariants = {
  enter: (direction) => ({ x: direction > 0 ? 40 : -40, opacity: 0 }),
  center: {
    x: 0,
    opacity: 1,
    transition: { type: "spring", stiffness: 400, damping: 30, duration: 0.3 },
  },
  exit: (direction) => ({
    x: direction < 0 ? 40 : -40,
    opacity: 0,
    transition: { type: "spring", stiffness: 400, damping: 30, duration: 0.2 },
  }),
};

const fadeVariants = {
  enter: { opacity: 0 },
  center: { opacity: 1, transition: { duration: 0.25 } },
  exit: { opacity: 0, transition: { duration: 0.18 } },
};

const WebsiteOnboarding = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { socket } = useNotifications();
  const [packages, setPackages] = useState([]);
  const [steps, setSteps] = useState(DEFAULT_WEBSITE_STEPS);
  const [websiteQuestions, setWebsiteQuestions] = useState(
    DEFAULT_WEBSITE_ONBOARDING_QUESTIONS,
  );
  const [questionsEnabled, setQuestionsEnabled] = useState(true);
  const [answers, setAnswers] = useState({});
  const [draftTouched, setDraftTouched] = useState(false);
  const [draftCompleted, setDraftCompleted] = useState(false);
  const [domainExtensions, setDomainExtensions] = useState([]);
  const [domainSettings, setDomainSettings] = useState({});
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");
  const [brandLogo, setBrandLogo] = useState("");
  const [domainData, setDomainData] = useState({
    domainType: "custom",
    customDomain: "",
    selectedExtension: ".co.za",
    existingDomain: "",
    subdomain: "",
    availability: null,
  });
  const [checkingDomain, setCheckingDomain] = useState(false);
  const [domainError, setDomainError] = useState("");
  const [paymentResult, setPaymentResult] = useState(null);
  const [gatewayCheckout, setGatewayCheckout] = useState(null);
  const [redirectingToGateway, setRedirectingToGateway] = useState(false);
  const [submittedRequestId, setSubmittedRequestId] = useState("");
  const paymentFormRef = useRef(null);
  const [selectedPackageId, setSelectedPackageId] = useState(
    searchParams.get("packageId") || "",
  );
  const [businessInfo, setBusinessInfo] = useState({
    businessName: location.state?.businessName || "",
    contactName: user?.FullName || "",
    email: user?.Email || "",
    phone: "",
    existingWebsite: "",
  });
  const [contract, setContract] = useState(null);
  const [contractAccepted, setContractAccepted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [expandedPackages, setExpandedPackages] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState("");

  const [step, setStep] = useState(0);
  const [maxStep, setMaxStep] = useState(0);
  const [domainPriceNoticeAcknowledged, setDomainPriceNoticeAcknowledged] =
    useState(false);
  const [direction, setDirection] = useState(1);

  const agreementNumber = useRef(`TF-WEB-${Date.now()}`);
  const invoiceNumber = useRef(
    `INV-WEB-${new Date().getFullYear()}-${Math.random().toString(36).slice(2, 10).toUpperCase()}`,
  );
  const errorRef = useRef(null);
  const formRef = useRef(null);
  const firstRender = useRef(true);
  const focusPending = useRef(false);

  const restoreDraft = useCallback((draft) => {
    setAnswers(draft.answers || {});
    setDomainData((current) => ({
      ...current,
      ...draft.domainData,
      availability: null,
    }));
    setSelectedPackageId(draft.selectedPackageId || "");
    setBusinessInfo((current) => ({ ...current, ...draft.businessInfo }));
    setContractAccepted(false);
    setSubmittedRequestId(draft.submittedRequestId || "");
    setSelectedPaymentMethod(draft.selectedPaymentMethod || "");
    setStep(Number.isInteger(draft.step) ? draft.step : 0);
    setMaxStep(Number.isInteger(draft.maxStep) ? draft.maxStep : 0);
    if (draft.invoiceNumber) invoiceNumber.current = draft.invoiceNumber;
    if (draft.agreementNumber) agreementNumber.current = draft.agreementNumber;
    setDraftTouched(true);
  }, []);

  const draftData = useMemo(
    () => ({
      answers,
      domainData: { ...domainData, availability: null },
      selectedPackageId,
      businessInfo,
      submittedRequestId,
      selectedPaymentMethod,
      step,
      maxStep,
      invoiceNumber: invoiceNumber.current,
      agreementNumber: agreementNumber.current,
    }),
    [
      answers,
      businessInfo,
      domainData,
      maxStep,
      selectedPackageId,
      selectedPaymentMethod,
      step,
      submittedRequestId,
    ],
  );

  const draftEnabled =
    String(user?.Role || "").toLowerCase() === "client" &&
    !user?.SupportSession;

  const {
    canSaveDraft,
    clearDraft,
    initialized: draftInitialized,
    saveDraft,
    status: draftStatus,
  } = useOnboardingDraft({
    api: websiteApi,
    data: draftData,
    enabled: draftEnabled,
    autosaveEnabled: !draftCompleted && !submitting,
    hasContent:
      draftTouched &&
      Boolean(
        businessInfo.businessName ||
        businessInfo.phone ||
        businessInfo.existingWebsite ||
        Object.keys(answers).length ||
        domainData.customDomain ||
        domainData.existingDomain ||
        domainData.subdomain ||
        (step > 0 && selectedPackageId),
      ),
    onRestore: restoreDraft,
    ready: !loading && Boolean(user),
    requestName: "website",
  });

  const renderSaveDraftButton = () =>
    draftEnabled ? (
      <button
        type="button"
        className="btn-secondary request-save-draft"
        data-tour="save-draft"
        aria-label="Save as draft"
        onClick={saveDraft}
        disabled={!canSaveDraft || submitting}
      >
        <i className="fas fa-floppy-disk" aria-hidden="true" />
        <span className="draft-label-full">Save as draft</span>
        <span className="draft-label-short">Save draft</span>
      </button>
    ) : null;

  useEffect(() => {
    if (gatewayCheckout && paymentFormRef.current) {
      paymentFormRef.current.requestSubmit();
    }
  }, [gatewayCheckout]);

  const refreshConfiguration = useCallback(async () => {
    try {
      const [
        packageResponse,
        configResponse,
        contractResponse,
        paymentResponse,
      ] = await Promise.all([
        websiteApi.getPublicPackages(),
        websiteApi.getOnboardingConfig(),
        onboardingApi.getContractSettings(),
        paymentApi.getMethods(),
      ]);
      const onboardingConfig = configResponse.data || {};
      setWebsiteQuestions(
        parseWebsiteOnboardingQuestions(onboardingConfig.questions),
      );
      setQuestionsEnabled(onboardingConfig.questionsEnabled !== false);
      const publicPackages = packageResponse.data || [];
      setPackages(publicPackages);
      setSelectedPackageId((current) =>
        publicPackages.some((item) => item.WebsitePackageID === current)
          ? current
          : publicPackages[0]?.WebsitePackageID || "",
      );
      if (Array.isArray(onboardingConfig.steps)) {
        setSteps(
          [...onboardingConfig.steps].sort(
            (left, right) => left.order - right.order,
          ),
        );
      }
      const availableDomains = onboardingConfig.domainExtensions || [];
      setDomainSettings(onboardingConfig.domainSettings || {});
      setDomainExtensions(availableDomains);
      setDomainData((current) => ({
        ...current,
        selectedExtension: availableDomains.some(
          (item) => item.extension === current.selectedExtension,
        )
          ? current.selectedExtension
          : availableDomains[0]?.extension || ".co.za",
      }));
      let configuredTemplates =
        contractResponse.data?.contractTemplates || DEFAULT_CONTRACT_TEMPLATES;
      if (typeof configuredTemplates === "string") {
        configuredTemplates = JSON.parse(configuredTemplates);
      }
      setBrandLogo(contractResponse.data?.brandLogo || "");
      const websiteContract =
        configuredTemplates.find((template) => template.id === "website") ||
        DEFAULT_CONTRACT_TEMPLATES.find(
          (template) => template.id === "website",
        );
      setContract(websiteContract || null);
      const methods = paymentResponse.data?.data ?? paymentResponse.data ?? [];
      const enabledMethods = Array.isArray(methods) ? methods : [];
      setPaymentMethods(enabledMethods);
      setSelectedPaymentMethod((current) =>
        enabledMethods.some((method) => method.code === current)
          ? current
          : enabledMethods.find((method) => method.code === "eft")?.code ||
            enabledMethods[0]?.code ||
            "",
      );
      setError("");
    } catch (loadError) {
      setError(
        loadError.response?.data?.error ||
          loadError.message ||
          "Could not load website onboarding.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshConfiguration();
  }, [refreshConfiguration]);

  useEffect(() => {
    if (!socket) return undefined;
    socket.on("platform:updated", refreshConfiguration);
    return () => socket.off("platform:updated", refreshConfiguration);
  }, [refreshConfiguration, socket]);

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [error]);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [step]);

  const selectedPackage = packages.find(
    (item) => item.WebsitePackageID === selectedPackageId,
  );
  const enabledSteps = steps.filter((item) => item.enabled);
  const visibleSteps = enabledSteps.length ? enabledSteps : steps;
  const disabledSteps = enabledSteps.length
    ? steps.filter((item) => !item.enabled)
    : [];
  const configuredQuestionSections = websiteQuestions.filter(
    (section) => Array.isArray(section.questions) && section.questions.length,
  );
  const showQuestionStep =
    questionsEnabled && configuredQuestionSections.length > 0;
  const wizardSteps = showQuestionStep
    ? [
        {
          id: "questions",
          name: "Questions",
          title: "Website Project Questions",
          description:
            "Tell us about your business goals and what you need from your website.",
          icon: "fa-clipboard-list",
        },
        ...visibleSteps,
      ]
    : visibleSteps;
  const LAST_STEP = wizardSteps.length - 1;
  const currentStepId = wizardSteps[step]?.id;
  const selectedExtension = domainExtensions.find(
    (item) => item.extension === domainData.selectedExtension,
  );
  const includedExtension = domainExtensions.find(
    (item) => item.extension === ".co.za",
  );
  const packageIncludesDomain = (selectedPackage?.Features || []).some(
    (feature) => /domain registration/i.test(String(feature)),
  );
  const selectedDomainBasePrice =
    domainData.domainType === "custom"
      ? Number(selectedExtension?.price || 0)
      : 0;
  const domainBaseAmount =
    domainData.domainType !== "custom"
      ? 0
      : packageIncludesDomain
        ? Math.max(
            0,
            selectedDomainBasePrice - Number(includedExtension?.price || 0),
          )
        : selectedDomainBasePrice;
  const domainTax =
    Math.round((domainBaseAmount * 0.15 + Number.EPSILON) * 100) / 100;
  const domainAmount =
    Math.round((domainBaseAmount + domainTax + Number.EPSILON) * 100) / 100;
  const domainAvailabilityCheckEnabled =
    domainSettings.enable_availability_check !== "false" &&
    domainSettings.domain_api_provider !== "none";
  const invoiceTotal = selectedPackage?.IsQuoteOnly
    ? null
    : Math.round(
        (Number(selectedPackage?.Price || 0) + domainAmount + Number.EPSILON) *
          100,
      ) / 100;
  const domainLabel =
    domainData.domainType === "custom"
      ? domainData.customDomain
        ? `${domainData.customDomain}${domainData.selectedExtension}`
        : ""
      : domainData.domainType === "existing"
        ? domainData.existingDomain
        : domainData.subdomain
          ? `${domainData.subdomain}.tooraflex.co.za`
          : "";

  useEffect(() => {
    setStep((current) => Math.min(current, LAST_STEP));
    setMaxStep((current) => Math.min(current, LAST_STEP));
  }, [LAST_STEP]);

  const agreementDate = useMemo(
    () => new Date().toLocaleDateString("en-ZA"),
    [],
  );

  const contractSnapshot = useMemo(() => {
    if (!contract) return null;
    const tokens = {
      "{{AGREEMENT_DATE}}": agreementDate,
      "{{AGREEMENT_NUMBER}}": agreementNumber.current,
      "{{BUSINESS_NAME}}": businessInfo.businessName || "[Business name]",
      "{{CLIENT_FULL_NAME}}": businessInfo.contactName || "[Client name]",
      "{{CONTACT_NAME}}": businessInfo.contactName || "[Contact name]",
      "{{PHONE}}": businessInfo.phone || "[Phone]",
      "{{EMAIL}}": businessInfo.email || "[Email]",
      "{{PACKAGE_NAME}}": selectedPackage?.Name || "[Selected package]",
      "{{DOMAIN}}":
        domainData.domainType === "custom"
          ? `${domainData.customDomain}${domainData.selectedExtension}`
          : domainData.domainType === "existing"
            ? domainData.existingDomain
            : `${domainData.subdomain}.tooraflex.co.za`,
      "{{MONTHLY_FEE}}": selectedPackage?.IsQuoteOnly
        ? "As quoted"
        : `R${Number(selectedPackage?.Price || 0).toFixed(2)} ${selectedPackage?.PriceLabel || ""}`,
      "{{SETUP_FEE}}": selectedPackage?.IsQuoteOnly
        ? "As quoted"
        : `R${Number(selectedPackage?.Price || 0).toFixed(2)}`,
      "{{DOMAIN_FEE}}": `R${domainAmount.toFixed(2)}`,
      "{{UPFRONT_DUE}}": selectedPackage?.IsQuoteOnly
        ? "As quoted"
        : `R${invoiceTotal.toFixed(2)}`,
    };
    const body = Object.entries(tokens).reduce(
      (text, [token, value]) => text.split(token).join(value),
      contract.content,
    );
    const clientBlock = [
      "",
      "",
      "------------------------------------------------------------",
      "Client details",
      `Contact name: ${businessInfo.contactName || "[Contact name]"}`,
      `Business: ${businessInfo.businessName || "[Business name]"}`,
      `Email: ${businessInfo.email || "[Email]"}`,
      `Package: ${selectedPackage?.Name || "[Selected package]"}`,
      `Agreement number: ${agreementNumber.current}`,
      `Date: ${agreementDate}`,
    ].join("\n");
    return {
      id: contract.id,
      name: contract.name,
      content: `${body}${clientBlock}`,
    };
  }, [
    agreementDate,
    businessInfo,
    contract,
    domainAmount,
    domainData,
    invoiceTotal,
    selectedPackage,
  ]);

  const toggleFeatures = (event, packageId) => {
    event.stopPropagation();
    setExpandedPackages((current) => ({
      ...current,
      [packageId]: !current[packageId],
    }));
  };

  const updateInfo = (field) => (event) => {
    const { value } = event.target;
    setBusinessInfo((current) => ({ ...current, [field]: value }));
    setFieldErrors((current) =>
      current[field] ? { ...current, [field]: undefined } : current,
    );
  };

  const setDomainType = (domainType) => {
    setDomainData((current) => ({
      ...current,
      domainType,
      availability: null,
    }));
    setDomainError("");
  };

  const checkSelectedDomain = async () => {
    const domainName =
      domainData.domainType === "existing"
        ? domainData.existingDomain.trim()
        : `${domainData.customDomain.trim()}${domainData.selectedExtension}`;
    if (domainData.domainType === "subdomain") return true;
    if (!domainAvailabilityCheckEnabled) {
      setDomainData((current) => ({
        ...current,
        availability: { available: true, skipped: true },
      }));
      setDomainError("");
      return true;
    }
    if (!domainName || domainName.startsWith(".")) {
      setDomainError("Enter a domain name before checking availability.");
      return false;
    }
    setCheckingDomain(true);
    setDomainError("");
    try {
      const response = await websiteApi.checkDomainAvailability(domainName);
      const result = response.data;
      if (result.serviceOffline || result.error) {
        setDomainError(
          result.error || "Domain availability could not be checked.",
        );
        return false;
      }
      setDomainData((current) => ({
        ...current,
        availability: result,
      }));
      if (!result.available) {
        setDomainError("That domain is unavailable. Choose another name.");
        return false;
      }
      return true;
    } catch (availabilityError) {
      setDomainError(
        availabilityError.response?.data?.error ||
          availabilityError.message ||
          "Domain availability could not be checked.",
      );
      return false;
    } finally {
      setCheckingDomain(false);
    }
  };

  const validateStepById = (stepId, { payLater = false } = {}) => {
    if (stepId === "package") {
      if (!selectedPackage) {
        setError("Select a package to continue.");
        return false;
      }
      return true;
    }
    if (stepId === "business") {
      const next = {};
      if (!businessInfo.businessName.trim()) {
        next.businessName = "Enter your business name.";
      }
      if (!businessInfo.contactName.trim()) {
        next.contactName = "Enter a contact name.";
      }
      if (!businessInfo.email.trim()) {
        next.email = "Enter your email address.";
      } else if (!EMAIL_PATTERN.test(businessInfo.email.trim())) {
        next.email = "Enter a valid email address.";
      }
      const site = businessInfo.existingWebsite.trim();
      if (site && !URL_PATTERN.test(site)) {
        next.existingWebsite = "Start the address with https://";
      }
      setFieldErrors(next);
      if (Object.keys(next).length) {
        setError("Fix the highlighted fields to continue.");
        return false;
      }
      return true;
    }
    if (stepId === "domain") {
      const domainName = `${domainData.customDomain.trim()}${domainData.selectedExtension}`;
      const availabilityCheckEnabled =
        domainSettings.enable_availability_check !== "false" &&
        domainSettings.domain_api_provider !== "none";
      const validDomain =
        domainData.domainType === "subdomain"
          ? /^[a-z0-9][a-z0-9-]{1,47}$/i.test(domainData.subdomain.trim())
          : domainData.domainType === "existing"
            ? /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(domainData.existingDomain.trim())
            : /^[a-z0-9-]+(?:\.[a-z0-9-]+)*\.[a-z]{2,63}$/i.test(domainName) &&
              (!availabilityCheckEnabled ||
                domainData.availability?.available === true);
      if (!validDomain) {
        setDomainError(
          domainData.domainType === "subdomain"
            ? "Enter a valid subdomain."
            : domainData.domainType === "existing"
              ? "Enter a valid domain name."
              : availabilityCheckEnabled
                ? "Check that your chosen domain is available before continuing."
                : "Enter a valid domain name.",
        );
        return false;
      }
      return true;
    }
    if (stepId === "agreement") {
      if (!contractSnapshot) {
        setError("The website contract isn't available. Contact support.");
        return false;
      }
      if (!contractAccepted) {
        setError("Accept the website contract to submit your onboarding.");
        return false;
      }
    }
    if (stepId === "questions") return true;
    if (
      stepId === "invoice" &&
      !payLater &&
      !selectedPackage?.IsQuoteOnly &&
      !selectedPaymentMethod
    ) {
      setError("Choose an available payment option to continue.");
      return false;
    }
    return true;
  };

  const validateStep = (index) => validateStepById(wizardSteps[index]?.id);

  const goTo = (target) => {
    if (target === step || target < 0 || target > LAST_STEP) return;
    focusPending.current = true;
    setDirection(target > step ? 1 : -1);
    setStep(target);
    setMaxStep((current) => Math.max(current, target));
  };

  const goNext = () => {
    if (!validateStep(step)) return;
    setError("");
    goTo(step + 1);
  };

  const goBack = () => {
    setError("");
    goTo(step - 1);
  };

  const handleStepClick = (index) => {
    if (index > maxStep || index === step) return;
    setError("");
    goTo(index);
  };

  const handlePackageSelect = (packageId) => {
    setSelectedPackageId(packageId);
    setError("");
    if (currentStepId === "package" && step < LAST_STEP) goTo(step + 1);
  };

  const handleSubmit = async (event, payLater = false) => {
    event.preventDefault();
    if (submitting) return;
    if (step < LAST_STEP) {
      goNext();
      return;
    }
    const failedStep = steps.find(
      (item) => !validateStepById(item.id, { payLater }),
    );
    if (failedStep) {
      const visibleIndex = wizardSteps.findIndex(
        (item) => item.id === failedStep.id,
      );
      goTo(visibleIndex >= 0 ? visibleIndex : LAST_STEP);
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      let requestId = submittedRequestId;
      if (!requestId) {
        const response = await websiteApi.createRequest({
          websitePackageId: selectedPackageId,
          domainPriceNoticeAcknowledged,
          ...businessInfo,
          projectDescription: "",
          answers,
          domain: domainData,
          invoiceNumber: invoiceNumber.current,
          contract: { ...contractSnapshot, accepted: true },
        });
        requestId =
          response.data?.data?.WebsiteRequestID ||
          response.data?.WebsiteRequestID;
        if (!requestId) {
          throw new Error("The website request was not created successfully.");
        }
        setSubmittedRequestId(requestId);
      }
      if (selectedPackage?.IsQuoteOnly) {
        await clearDraft();
        setDraftCompleted(true);
        navigate(`/website-request-status/${requestId}`);
        return;
      }
      if (payLater) {
        await clearDraft();
        setDraftCompleted(true);
        navigate(`/website-request-status/${requestId}`);
        return;
      }
      await websiteApi.saveDraft({
        ...draftData,
        submittedRequestId: requestId,
      });
      const paymentResponse = await paymentApi.startWebsitePayment(
        requestId,
        selectedPaymentMethod,
      );
      const paymentData = paymentResponse.data?.data || paymentResponse.data;
      setPaymentResult(paymentData.payment);
      if (paymentData.checkout) {
        setGatewayCheckout(paymentData.checkout);
        setRedirectingToGateway(true);
      } else {
        await clearDraft();
        setDraftCompleted(true);
        navigate(`/website-request-status/${requestId}`);
      }
      if (paymentData.checkout) {
        await clearDraft();
        setDraftCompleted(true);
      }
    } catch (submitError) {
      setError(
        submitError.response?.data?.error ||
          "Couldn't submit your onboarding. Try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !draftInitialized) {
    return (
      <main className="request-store-page website-request-page">
        <div className="loading-container" role="status">
          <div className="spinner" />
          <p>Loading website onboarding...</p>
        </div>
      </main>
    );
  }

  const packageStep = (
    <>
      {packages.length === 0 ? (
        <div className="no-packages-message">
          <i className="fas fa-box-open" />
          <h3>No Packages Available</h3>
          <p>
            No website packages are available right now. Check back soon or
            contact support.
          </p>
        </div>
      ) : (
        <>
          <div
            className="package-grid-compact"
            data-tour="package-grid"
            role="radiogroup"
            aria-label="Website package"
          >
            {packages.map((pkg) => {
              const isSelected = selectedPackageId === pkg.WebsitePackageID;
              const features = pkg.Features || [];
              const expanded = Boolean(expandedPackages[pkg.WebsitePackageID]);
              const visibleFeatures = expanded
                ? features
                : features.slice(0, FEATURE_LIMIT);
              const hiddenCount = features.length - FEATURE_LIMIT;
              return (
                <div
                  key={pkg.WebsitePackageID}
                  role="radio"
                  aria-checked={isSelected}
                  tabIndex={0}
                  className={`package-card-compact ${isSelected ? "selected" : ""} ${pkg.IsPopular ? "popular" : ""}`}
                  onClick={() => handlePackageSelect(pkg.WebsitePackageID)}
                  onKeyDown={(event) => {
                    if (event.target !== event.currentTarget) return;
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handlePackageSelect(pkg.WebsitePackageID);
                    }
                  }}
                >
                  {pkg.IsPopular && (
                    <div className="popular-badge-compact">POPULAR</div>
                  )}
                  <div className="package-card-header-compact">
                    <h4>{pkg.Name}</h4>
                    {pkg.IsQuoteOnly && (
                      <span className="package-tier-compact">Quote</span>
                    )}
                  </div>
                  {pkg.Description && (
                    <p className="package-description-compact">
                      {pkg.Description}
                    </p>
                  )}
                  <div className="package-price-compact">
                    {formatPrice(pkg)}
                    {pkg.PriceLabel && !pkg.IsQuoteOnly && (
                      <span> / {pkg.PriceLabel}</span>
                    )}
                  </div>
                  {features.length > 0 && (
                    <ul className="package-features-compact">
                      {visibleFeatures.map((feature, index) => (
                        <li key={`${feature}-${index}`}>
                          <i className="fas fa-check" aria-hidden="true" />
                          <span>{feature}</span>
                        </li>
                      ))}
                      {hiddenCount > 0 && (
                        <li className="more-features">
                          <button
                            type="button"
                            className="legal-link-button"
                            aria-expanded={expanded}
                            onClick={(event) =>
                              toggleFeatures(event, pkg.WebsitePackageID)
                            }
                          >
                            {expanded ? "Show less" : `+${hiddenCount} more`}
                          </button>
                        </li>
                      )}
                    </ul>
                  )}
                  {isSelected && (
                    <div className="selected-indicator-compact">
                      <i className="fas fa-check-circle" /> Selected
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div
            className="selected-package-summary-compact"
            data-tour="package-summary"
          >
            {selectedPackage ? (
              <>
                <div className="summary-icon-compact">
                  <i className="fas fa-check-circle" />
                </div>
                <div className="summary-content-compact">
                  <strong>{selectedPackage.Name} Selected</strong>
                  <span>
                    {formatPrice(selectedPackage)}
                    {selectedPackage.PriceLabel &&
                      !selectedPackage.IsQuoteOnly &&
                      ` / ${selectedPackage.PriceLabel}`}
                  </span>
                </div>
              </>
            ) : (
              <div className="summary-content-compact">
                <strong>No package selected yet</strong>
                <span>Select a package above to continue.</span>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );

  const businessStep = (
    <div className="request-form">
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="wo-business">
            Business Name <span className="required">*</span>
          </label>
          <input
            id="wo-business"
            type="text"
            value={businessInfo.businessName}
            onChange={updateInfo("businessName")}
            placeholder="Your business name"
            autoComplete="organization"
            className={fieldErrors.businessName ? "error" : ""}
            required
            aria-invalid={Boolean(fieldErrors.businessName)}
            aria-describedby="wo-err-business"
          />
          <FieldError id="wo-err-business" message={fieldErrors.businessName} />
        </div>
        <div className="form-group">
          <label htmlFor="wo-contact">
            Contact Name <span className="required">*</span>
          </label>
          <input
            id="wo-contact"
            type="text"
            value={businessInfo.contactName}
            onChange={updateInfo("contactName")}
            placeholder="Full name"
            autoComplete="name"
            className={fieldErrors.contactName ? "error" : ""}
            required
            aria-invalid={Boolean(fieldErrors.contactName)}
            aria-describedby="wo-err-contact"
          />
          <FieldError id="wo-err-contact" message={fieldErrors.contactName} />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="wo-email">
            Email Address <span className="required">*</span>
          </label>
          <input
            id="wo-email"
            type="email"
            value={businessInfo.email}
            onChange={updateInfo("email")}
            placeholder="you@email.com"
            autoComplete="email"
            className={fieldErrors.email ? "error" : ""}
            required
            aria-invalid={Boolean(fieldErrors.email)}
            aria-describedby="wo-err-email"
          />
          <FieldError id="wo-err-email" message={fieldErrors.email} />
        </div>
        <div className="form-group">
          <label htmlFor="wo-phone">Phone Number</label>
          <input
            id="wo-phone"
            type="tel"
            value={businessInfo.phone}
            onChange={updateInfo("phone")}
            placeholder="+27 82 123 4567"
            autoComplete="tel"
          />
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="wo-site">Current Website (if any)</label>
        <input
          id="wo-site"
          type="url"
          value={businessInfo.existingWebsite}
          onChange={updateInfo("existingWebsite")}
          placeholder="https://yourwebsite.com"
          className={fieldErrors.existingWebsite ? "error" : ""}
          aria-invalid={Boolean(fieldErrors.existingWebsite)}
          aria-describedby="wo-err-site"
        />
        <FieldError id="wo-err-site" message={fieldErrors.existingWebsite} />
      </div>
    </div>
  );

  const projectQuestionsStep = (
    <section className="website-project-questions">
      <BusinessProfileStep
        initialData={answers}
        questions={configuredQuestionSections}
        onChange={setAnswers}
        onFinish={goNext}
        onBackFromFirstPage={goBack}
        footerAction={renderSaveDraftButton()}
        questionsPerPage={3}
        nextLabel="Next"
        finishLabel="Proceed"
        showBackArrow
      />
    </section>
  );

  const domainPriceText =
    packageIncludesDomain && domainBaseAmount === 0
      ? "Included in your selected package"
      : `R${domainAmount.toFixed(2)} incl. VAT per year`;

  const domainStep = (
    <div className="step-form">
      {domainError && (
        <div className="error-text domain-error" role="alert">
          {domainError}
        </div>
      )}

      <div className="domain-options" data-tour="domain-options">
        <button
          type="button"
          className={`domain-option ${domainData.domainType === "custom" ? "selected" : ""}`}
          aria-pressed={domainData.domainType === "custom"}
          onClick={() => setDomainType("custom")}
        >
          <span className="domain-option-icon" aria-hidden="true">
            <i className="fas fa-globe" />
          </span>
          <span className="domain-option-copy">
            <strong>Register a new domain</strong>
            <small>Choose a name and extension for your website.</small>
          </span>
          <span className="domain-price-tag">
            {packageIncludesDomain && domainBaseAmount === 0
              ? "Domain included"
              : `From R${domainAmount.toFixed(2)}/year`}
          </span>
          <span className="domain-option-check" aria-hidden="true">
            <i className="fas fa-check" />
          </span>
        </button>
        <button
          type="button"
          className={`domain-option ${domainData.domainType === "existing" ? "selected" : ""}`}
          aria-pressed={domainData.domainType === "existing"}
          onClick={() => setDomainType("existing")}
        >
          <span className="domain-option-icon" aria-hidden="true">
            <i className="fas fa-link" />
          </span>
          <span className="domain-option-copy">
            <strong>I already own a domain</strong>
            <small>Enter the domain you already use. No charge.</small>
          </span>
          <span className="domain-option-example">example.com</span>
          <span className="domain-option-check" aria-hidden="true">
            <i className="fas fa-check" />
          </span>
        </button>
        <button
          type="button"
          className={`domain-option ${domainData.domainType === "subdomain" ? "selected" : ""}`}
          aria-pressed={domainData.domainType === "subdomain"}
          onClick={() => setDomainType("subdomain")}
        >
          <span className="domain-option-icon" aria-hidden="true">
            <i className="fas fa-sitemap" />
          </span>
          <span className="domain-option-copy">
            <strong>Use a Toora Flex subdomain</strong>
            <small>A free address on our domain.</small>
          </span>
          <span className="domain-option-example">name.tooraflex.co.za</span>
          <span className="domain-option-check" aria-hidden="true">
            <i className="fas fa-check" />
          </span>
        </button>
      </div>

      {domainData.domainType === "custom" && (
        <div className="custom-domain-section" data-tour="custom-domain">
          <label className="domain-field-label" htmlFor="wo-custom-domain">
            Enter the name you want
          </label>
          <p className="domain-field-help">
            Type the name only, without dots or an extension. For example, type{" "}
            <strong>mybusiness</strong> to make mybusiness.co.za.
          </p>
          <div className="domain-input-group">
            <div className="domain-input-wrapper">
              <input
                type="text"
                id="wo-custom-domain"
                value={domainData.customDomain}
                onChange={(event) => {
                  setDomainData({
                    ...domainData,
                    customDomain: event.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9-]/g, ""),
                    availability: null,
                  });
                  setDomainError("");
                }}
                placeholder="yourbusiness"
                aria-label="Preferred domain name"
              />
              <select
                className="extension-select"
                value={domainData.selectedExtension}
                onChange={(event) => {
                  setDomainData({
                    ...domainData,
                    selectedExtension: event.target.value,
                    availability: null,
                  });
                  setDomainError("");
                }}
                aria-label="Choose a domain extension"
              >
                {domainExtensions.map((extension) => (
                  <option key={extension.extension} value={extension.extension}>
                    {extension.extension} — R
                    {Number(extension.price || 0).toFixed(2)}/year
                  </option>
                ))}
              </select>
            </div>
            {domainAvailabilityCheckEnabled && (
              <button
                type="button"
                className="verify-btn"
                data-tour="availability-check"
                onClick={checkSelectedDomain}
                disabled={checkingDomain || !domainData.customDomain}
              >
                {checkingDomain ? "Checking domain..." : "Check availability"}
              </button>
            )}
          </div>

          {checkingDomain && (
            <div className="checking-status" role="status">
              <span className="spinner-small" /> Checking availability...
            </div>
          )}

          {domainAvailabilityCheckEnabled &&
            domainData.availability?.available && (
              <div className="availability-status available" role="status">
                <span>✅</span>
                <strong>{domainLabel}</strong> is available!
                <span className="domain-price">{domainPriceText}</span>
              </div>
            )}

          {!domainAvailabilityCheckEnabled && (
            <div className="availability-disabled-notice" role="status">
              <i className="fas fa-info-circle" />
              Domain availability checks are currently disabled.
            </div>
          )}

          <div
            className="domain-selection-summary"
            aria-live="polite"
            data-tour="domain-total"
          >
            <span>Your chosen domain</span>
            <strong>{domainLabel || "yourname"}</strong>
            <span>{domainPriceText}</span>
          </div>
          <p className="hint-text" style={{ marginTop: 12 }}>
            {packageIncludesDomain
              ? "Your package includes a .co.za domain allowance; any difference for the selected extension is charged."
              : "The selected domain's annual registration price will be added to your invoice."}
          </p>
        </div>
      )}

      {domainData.domainType === "existing" && (
        <div className="form-group domain-entry-card">
          <label htmlFor="wo-existing-domain">Your existing domain *</label>
          <input
            type="text"
            id="wo-existing-domain"
            value={domainData.existingDomain}
            onChange={(event) => {
              setDomainData({
                ...domainData,
                existingDomain: event.target.value
                  .trim()
                  .toLowerCase()
                  .replace(/^https?:\/\//, "")
                  .replace(/^www\./, "")
                  .split("/")[0],
                availability: null,
              });
              setDomainError("");
            }}
            placeholder="yourbusiness.co.za"
            autoComplete="url"
          />
          <small className="help-text">
            Leave out “https://” and “www”. No domain registration fee is
            charged.
          </small>
        </div>
      )}

      {domainData.domainType === "subdomain" && (
        <div className="form-group domain-entry-card">
          <label htmlFor="wo-subdomain">Preferred subdomain</label>
          <div className="subdomain-input">
            <input
              type="text"
              id="wo-subdomain"
              value={domainData.subdomain}
              onChange={(event) => {
                setDomainData({
                  ...domainData,
                  subdomain: event.target.value
                    .toLowerCase()
                    .replace(/[^a-z0-9-]/g, ""),
                });
                setDomainError("");
              }}
              placeholder="yourbusiness"
            />
            <span className="subdomain-suffix">.tooraflex.co.za</span>
          </div>
          <small className="help-text">No domain registration charge.</small>
        </div>
      )}
    </div>
  );

  const contractStep = (
    <div className="step-form">
      {contractSnapshot ? (
        <div className="agreement-container" data-tour="agreement-document">
          <div className="agreement-content">
            <div className="contract-document-brand">
              {brandLogo ? (
                <img src={brandLogo} alt="Toora Flex" />
              ) : (
                <span className="invoice-logo">TF</span>
              )}
              <span>Website Contract</span>
            </div>
            <h3>{contractSnapshot.name}</h3>
            <dl className="wr-parties">
              <div>
                <dt>Client</dt>
                <dd>{businessInfo.contactName || "Not added yet"}</dd>
              </div>
              <div>
                <dt>Business</dt>
                <dd>{businessInfo.businessName || "Not added yet"}</dd>
              </div>
              <div>
                <dt>Email</dt>
                <dd>{businessInfo.email || "Not added yet"}</dd>
              </div>
              <div>
                <dt>Package</dt>
                <dd>{selectedPackage?.Name || "Not selected"}</dd>
              </div>
              <div>
                <dt>Agreement number</dt>
                <dd>{agreementNumber.current}</dd>
              </div>
              <div>
                <dt>Date</dt>
                <dd>{agreementDate}</dd>
              </div>
            </dl>
            <pre
              className="contract-document-text"
              tabIndex={0}
              aria-label={contractSnapshot.name}
            >
              {contractSnapshot.content}
            </pre>
          </div>
          <div
            className="agreement-acceptance"
            data-tour="agreement-acceptance"
          >
            <label className="checkbox-label agreement-checkbox">
              <input
                type="checkbox"
                checked={contractAccepted}
                onChange={(event) => {
                  setContractAccepted(event.target.checked);
                  setError("");
                }}
              />
              <span>
                <strong>
                  I have read and agree to the terms and conditions
                </strong>
                <small>
                  I, {businessInfo.contactName || "the client"}, agree to the{" "}
                  {contractSnapshot.name} on behalf of{" "}
                  {businessInfo.businessName || "my business"}.
                </small>
              </span>
            </label>
          </div>
        </div>
      ) : (
        <div className="no-packages-message">
          <i className="fas fa-file-circle-exclamation" />
          <h3>Contract unavailable</h3>
          <p>
            The website contract isn't available. Contact support to continue.
          </p>
        </div>
      )}
    </div>
  );

  const selectedMethod = paymentMethods.find(
    (method) => method.code === selectedPaymentMethod,
  );
  const isQuote = Boolean(selectedPackage?.IsQuoteOnly);
  const domainCell = isQuote
    ? "Included in quote"
    : domainAmount === 0
      ? "No charge"
      : `R${domainAmount.toFixed(2)}`;

  const paymentStep = (
    <div className="invoice-container" data-tour="invoice-summary">
      <div className="invoice-header">
        <div className="invoice-brand">
          {brandLogo ? (
            <img
              className="invoice-brand-logo"
              src={brandLogo}
              alt="Toora Flex"
            />
          ) : (
            <div className="invoice-logo">TF</div>
          )}
          <div>
            <h3>Toora Flex</h3>
            <small>Website Services</small>
          </div>
        </div>
        <div className="invoice-details">
          <div className="invoice-number">
            <span>Invoice #</span>
            <strong>{invoiceNumber.current}</strong>
          </div>
          <div className="invoice-date">
            <span>Date</span>
            <strong>{agreementDate}</strong>
          </div>
        </div>
      </div>

      <div className="invoice-bill-to">
        <div className="bill-to-section">
          <h4>Bill To:</h4>
          <p>
            <strong>{businessInfo.businessName || "Client"}</strong>
          </p>
          <p>Client: {businessInfo.contactName || "N/A"}</p>
          <p>{businessInfo.email || "N/A"}</p>
          <p>{businessInfo.phone || "N/A"}</p>
        </div>
        <div className="bill-to-section">
          <h4>Issued By:</h4>
          <p>
            <strong>Toora Flex (Pty) Ltd</strong>
          </p>
          <p>Reg No: 2026/036648/07</p>
          <p>367 Helen Joseph Street, Pretoria Central</p>
        </div>
      </div>

      <table className="invoice-table" data-tour="invoice-table">
        <thead>
          <tr>
            <th>Description</th>
            <th>Qty</th>
            <th>Unit Price</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Website package: {selectedPackage?.Name || "Not selected"}</td>
            <td>1</td>
            <td>{formatPrice(selectedPackage)}</td>
            <td>{formatPrice(selectedPackage)}</td>
          </tr>
          <tr>
            <td>Domain ({domainLabel || "Not selected"})</td>
            <td>1</td>
            <td>{domainCell}</td>
            <td>{domainCell}</td>
          </tr>
          <tr className="invoice-total">
            <td colSpan="3">
              <strong>TOTAL DUE</strong>
            </td>
            <td>
              <strong>
                {invoiceTotal === null
                  ? "Custom quote"
                  : `R${invoiceTotal.toFixed(2)}`}
              </strong>
            </td>
          </tr>
        </tbody>
      </table>

      <div className="invoice-payment-details">
        <h4>{isQuote ? "Quote" : "Payment option"}</h4>
        {isQuote ? (
          <p className="payment-note">
            This package is quote-based. Submit your request and our team will
            confirm the final invoice before payment is due.
          </p>
        ) : (
          <>
            <div className="payment-requirement-note" role="note">
              <strong>Payment is required before website work can begin.</strong>
              <p>
                Choose Pay now to submit your request and pay now. Choose
                Submit &amp; pay later to submit the request and pay from its
                status page when you are ready. Save as draft only saves your
                progress; return to this invoice to choose a payment option.
              </p>
            </div>
            {paymentMethods.length ? (
              <>
            <fieldset
              className="payment-method-selector"
              data-tour="payment-methods"
            >
              <legend>Select how you want to pay</legend>
              <div className="payment-methods">
                {paymentMethods.map((method) => (
                  <label
                    className={`payment-option ${selectedPaymentMethod === method.code ? "selected" : ""}`}
                    key={method.code}
                  >
                    <input
                      type="radio"
                      name="websitePaymentMethod"
                      value={method.code}
                      checked={selectedPaymentMethod === method.code}
                      onChange={() => setSelectedPaymentMethod(method.code)}
                    />
                    {method.paymentImage && (
                      <img
                        className="payment-option-image"
                        src={method.paymentImage}
                        alt=""
                      />
                    )}
                    <span className="payment-info">
                      <strong>{method.displayName}</strong>
                      <small>
                        {method.online ? "Online payment" : "Manual payment"}
                      </small>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
            {selectedMethod?.instructions && (
              <div className="bank-details-card">
                <div className="bank-details-heading">
                  <span className="bank-details-icon" aria-hidden="true">
                    <i className="fas fa-university" />
                  </span>
                  <div>
                    <h5>{selectedMethod.displayName} instructions</h5>
                    <p>Use these details when making your payment.</p>
                  </div>
                </div>
                <div className="bank-details-instructions">
                  {selectedMethod.instructions}
                </div>
              </div>
            )}
              </>
            ) : (
              <p className="payment-note" role="alert">
                No payment methods are currently available. Please contact
                support to continue.
              </p>
            )}
          </>
        )}
        {redirectingToGateway && (
          <p className="payment-gateway-redirect" role="status">
            <span className="spinner-small" aria-hidden="true" />
            Redirecting to {paymentResult?.method || "the payment gateway"}...
          </p>
        )}
      </div>
    </div>
  );

  const stepContents = {
    questions: projectQuestionsStep,
    package: packageStep,
    business: businessStep,
    domain: domainStep,
    agreement: contractStep,
    invoice: paymentStep,
  };

  const renderStepIndicator = () => {
    const total = wizardSteps.length;
    if (total === 0) return null;
    return (
      <div className="step-indicator-compact">
        <p className="step-indicator-caption">Website setup steps</p>
        <div className="step-progress-bar">
          <div
            className="step-progress-fill"
            style={{
              width: `${total <= 1 ? 100 : (step / (total - 1)) * 100}%`,
            }}
          />
        </div>
        <div className="step-dots">
          {wizardSteps.map((item, index) => {
            const isActive = index === step;
            const isCompleted = index < step;
            const reachable = index <= maxStep && !isActive;
            return (
              <div
                key={item.id}
                className={`step-dot ${isActive ? "active" : ""} ${isCompleted ? "completed" : ""}`}
                role={reachable ? "button" : undefined}
                tabIndex={reachable ? 0 : undefined}
                aria-current={isActive ? "step" : undefined}
                onClick={() => handleStepClick(index)}
                onKeyDown={(event) => {
                  if (
                    reachable &&
                    (event.key === "Enter" || event.key === " ")
                  ) {
                    event.preventDefault();
                    handleStepClick(index);
                  }
                }}
                style={{ cursor: reachable ? "pointer" : "default" }}
              >
                <span className="step-dot-number">
                  <StepIcon icon={item.icon} fallback={index + 1} />
                </span>
                <span className="step-dot-label">
                  Step {index + 1}: {item.name || "Setup"}
                </span>
                {isCompleted && <span className="step-dot-check">✓</span>}
              </div>
            );
          })}
        </div>
        <div className="step-counter">
          Step {step + 1} of {total}
          {wizardSteps[step]?.name ? ` · ${wizardSteps[step].name}` : ""}
        </div>
      </div>
    );
  };

  const renderStepContent = () => {
    const item = wizardSteps[step];
    if (!item) return null;
    return (
      <motion.div
        key={item.id}
        custom={direction}
        variants={item.id === "questions" ? fadeVariants : slideVariants}
        initial="enter"
        animate="center"
        exit="exit"
        className="step-content"
        data-tour="step-content"
        onAnimationComplete={(definition) => {
          if (definition === "center" && focusPending.current) {
            focusPending.current = false;
            document
              .getElementById(`website-onboarding-title-${step}`)
              ?.focus({ preventScroll: true });
          }
        }}
      >
        <div className="step-header-compact">
          <h2 id={`website-onboarding-title-${step}`} tabIndex={-1}>
            {item.title}
          </h2>
          <p>{item.description}</p>
        </div>
        {stepContents[item.id]}
        {step === LAST_STEP && disabledSteps.length > 0 && (
          <div className="wr-disabled-steps">
            {disabledSteps.map((disabledStep) => (
              <section
                key={disabledStep.id}
                className="wr-disabled-step"
                aria-labelledby={`website-onboarding-disabled-title-${disabledStep.id}`}
              >
                <div className="step-header-compact">
                  <h3
                    id={`website-onboarding-disabled-title-${disabledStep.id}`}
                  >
                    {disabledStep.title}
                  </h3>
                  <p>{disabledStep.description}</p>
                </div>
                {stepContents[disabledStep.id]}
              </section>
            ))}
          </div>
        )}
      </motion.div>
    );
  };

  const nextDisabled = currentStepId === "package";

  const navClassName = [
    "step-navigation-compact",
    step > 0 ? "has-back" : "no-back",
    draftEnabled ? "has-draft" : "no-draft",
    step === LAST_STEP ? "is-final" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <main
      className="request-store-page website-request-page"
      onChangeCapture={() => setDraftTouched(true)}
      onClickCapture={() => setDraftTouched(true)}
    >
      {!domainPriceNoticeAcknowledged && (
        <DomainPriceNoticeModal
          onAcknowledge={() => setDomainPriceNoticeAcknowledged(true)}
          onClose={() => navigate("/dashboard", { replace: true })}
        />
      )}
      {(submitting || redirectingToGateway) && (
        <div
          className="payment-loading-overlay"
          role="status"
          aria-live="polite"
        >
          <span className="payment-loading-spinner" aria-hidden="true" />
          <p>
            {redirectingToGateway
              ? `Redirecting to ${paymentResult?.method || "the payment gateway"}...`
              : "Processing your request..."}
          </p>
        </div>
      )}

      <div className="request-store-header-compact">
        <h1>Start Your Website</h1>
        <p>
          {showQuestionStep
            ? "Start with a few project questions, then choose your package, domain, agreement and invoice."
            : "Choose a package and set up your website in minutes."}
        </p>
      </div>

      {renderStepIndicator()}

      {error && (
        <div
          className="request-alert request-alert-error"
          role="alert"
          ref={errorRef}
        >
          <i className="fas fa-exclamation-circle" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      <form
        className="step-content-wrapper"
        id="website-onboarding-form"
        ref={formRef}
        onSubmit={handleSubmit}
        noValidate
      >
        <AnimatePresence mode="wait" custom={direction}>
          {renderStepContent()}
        </AnimatePresence>

        {currentStepId !== "questions" && (
          <div className={navClassName}>
            {step > 0 && (
              <button
                type="button"
                className="btn-nav back"
                aria-label="Back"
                onClick={goBack} //come back
              >
                <i className="fas fa-arrow-left" />{"  "}
                <span className="btn-label">Back</span>
              </button>
            )}
            {draftStatus && (
              <span className="request-draft-feedback" role="status">
                {draftStatus}
              </span>
            )}
            <div className="nav-spacer" />
            {step === LAST_STEP ? (
              <div className="request-next-actions request-final-actions">
                {!isQuote && (
                  <button
                    type="button"
                    className="btn-secondary pay-later-btn"
                    data-tour="pay-later"
                    onClick={(event) => handleSubmit(event, true)}
                    disabled={submitting || !contractSnapshot}
                    aria-label="Submit website request and pay later"
                  >
                    Submit &amp; pay later
                  </button>
                )}
                <button
                  type="submit"
                  className="btn-primary submit-btn-final"
                  data-tour="submit-request"
                  disabled={
                    submitting || !contractSnapshot || Boolean(gatewayCheckout)
                  }
                >
                  {submitting ? (
                    <>
                      <span className="spinner-small" /> Processing...
                    </>
                  ) : redirectingToGateway ? (
                    <>
                      <span className="spinner-small" /> Redirecting...
                    </>
                  ) : isQuote ? (
                    "Submit for quote"
                  ) : (
                    "Pay now"
                  )}
                </button>
              </div>
            ) : (
              <div className="request-next-actions">
                <button
                  type="button"
                  className="btn-nav next"
                  data-tour="next-button"
                  onClick={goNext}
                  disabled={nextDisabled}
                >
                  Next <i className="fas fa-arrow-right" />
                </button>
                {renderSaveDraftButton()}
              </div>
            )}
          </div>
        )}
      </form>

      {gatewayCheckout && (
        <form
          ref={paymentFormRef}
          method="post"
          action={gatewayCheckout.action}
          className="gateway-checkout-form"
          aria-hidden="true"
        >
          {Object.entries(gatewayCheckout.fields).map(([name, value]) => (
            <input key={name} type="hidden" name={name} value={value} />
          ))}
        </form>
      )}
    </main>
  );
};

export default WebsiteOnboarding;
